"use client";

import { ArrowLongLeftIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PaystackButton } from "react-paystack";
import {
  generatePaystackReference,
  generateSquadReference,
  verifyPaystackPayment,
  verifySquadPayment,
} from "@capex/services/apiQueries/payments";
import { QuoteData } from "@capex/types";
import { GenericButton } from "../general/button.component";
import { useRouter } from "next/navigation";
import { PageFrameComponent } from "../general/page-frame.component";

export const ChoosePaymentComponent = ({
  setStage,
  quote,
  firstName,
  lastName,
  isLoggedIn,
}: {
  isLoggedIn: boolean;
  setStage: (stage: number) => void;
  quote: QuoteData;
  firstName: string;
  lastName: string;
}) => {
  const totalPremium =
    quote.productCode !== "CSP"
      ? quote.premium
      : quote.premium + quote.lifeCoverPremium;
  const router = useRouter();
  const [paystackReference, setPaystackReference] = useState<
    string | undefined
  >(undefined);
  const [squadReference, setSquadReference] = useState<string | undefined>(
    undefined
  );
  const [disablePaymentButtons, setDisablePaymentButtons] =
    useState<boolean>(false);

  async function SquadPay() {
    if (disablePaymentButtons) return;
    // @ts-ignore
    const squad = window?.squad;
    const referenceNo = await generateSquadReference(quote.quoteNo);
    setSquadReference(referenceNo);
    const squadInstance = new squad({
      onClose: () => console.log("Widget closed successfully"),
      onLoad: () => console.log("Widget loaded successfully"),
      onSuccess: (data: { transaction_ref: string }) =>
        onSquadPaymentSuccess(data),
      key: process.env.NEXT_PUBLIC_SQUADPAY_PUBLIC_KEY ?? "",
      email: quote.email,
      amount: totalPremium * 100,
      transaction_ref: squadReference,
      Customer_name: `${firstName} ${lastName}`,
      currency_code: "NGN",
      metadata: {
        custom_fields: [
          {
            display_name: "Mobile Number",
            variable_name: "mobile_number",
            value: quote.phoneNumber,
          },
        ],
      },
    });
    squadInstance.setup();
    squadInstance.open();
  }

  const onSquadPaymentSuccess = async (data: { transaction_ref: string }) => {
    const reference = data?.transaction_ref;
    if (!reference) {
      toast.error("We are unable to verify your payment. Kindly contact us.");
      return;
    }
    setDisablePaymentButtons(true);
    try {
      const res = await verifySquadPayment({
        referenceNo: reference,
        quoteNo: quote.quoteNo,
        product: quote.product,
        amount: totalPremium,
        firstName,
        lastName,
      });
      if (
        res.statusMessage ===
        `Payment record for ${res.paymentReference} created successfully!`
      ) {
        isLoggedIn ? router.push("/console/kyc") : setStage(5);
      } else {
        toast.error("We are unable to verify your payment. Kindly contact us.");
      }
    } catch (error) {
      toast.error("An unexpected error occurred.");
    }
  };

  const onPaymentSuccess = async (data: { reference: string }) => {
    const reference = data?.reference;
    if (!reference) {
      toast.error("We are unable to verify your payment. Kindly contact us.");
      return;
    }
    setDisablePaymentButtons(true);
    try {
      const res = await verifyPaystackPayment({
        referenceNo: reference,
        quoteNo: quote.quoteNo,
        product: quote.product,
        amount: totalPremium,
        firstName,
        lastName,
      });
      if (
        res.statusMessage ===
        `Payment record for ${res.paymentReference} created successfully!`
      ) {
        isLoggedIn ? router.push("/console/kyc") : setStage(5);
      } else {
        toast.error("We are unable to verify your payment. Kindly contact us.");
      }
    } catch (error) {
      toast.error("An unexpected error occurred.");
    }
  };

  const handlePurchase = useCallback(async () => {
    const referenceNo = await generatePaystackReference(quote.quoteNo);
    setPaystackReference(referenceNo);
  }, [quote.quoteNo]);

  useEffect(() => {
    if (paystackReference) {
      if (disablePaymentButtons) return;
      const psBtn = document.querySelector(".PaystackButtonID");
      // @ts-ignore
      psBtn.click();
    }
  }, [paystackReference, disablePaymentButtons]);

  return (
    <PageFrameComponent>
      <div className="h-full grid grid-cols-1 place-content-center w-full">
        <div className="text-center mb-9">
          <p className="font-medium text-2xl md:text-3xl text-secondary">
            Choose a payment method
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-x-8 gap-y-5 mb-10">
          <div
            className={`bg-white w-full md:w-[280px] h-[135px] flex items-center justify-center 
            gap-6 rounded-lg shadow hover:shadow-md border border-[#eee] ${
              disablePaymentButtons ? "cursor-not-allowed" : "cursor-pointer"
            } ${disablePaymentButtons && "animate-pulse"}`}
            onClick={handlePurchase}
          >
            <Image
              src="/img/paystack_logo.png"
              width={45}
              height={45}
              alt="paystack_logo"
            />
            <p className="text-2xl font-normal text-text-dark">Paystack</p>
          </div>
          <div
            className={`bg-white w-full md:w-[280px] h-[135px] flex items-center justify-center
           gap-6 rounded-lg shadow hover:shadow-md border border-[#eee] ${
             disablePaymentButtons ? "cursor-not-allowed" : "cursor-pointer"
           } ${disablePaymentButtons && "animate-pulse"}`}
            onClick={SquadPay}
          >
            <Image
              src="/img/squad_logo.png"
              width={45}
              height={45}
              alt="squad_logo"
            />
            <p className="text-2xl font-normal text-text-dark">Squad</p>
          </div>
        </div>
        <GenericButton
          className="bg-accent flex justify-center items-center gap-x-3 !w-[155px] md:!w-[230px] lg:!w-[245px] mx-auto"
          onClick={() => setStage(3)}
        >
          <ArrowLongLeftIcon className="w-6 h-6" />
          Back
        </GenericButton>

        <PaystackButton
          publicKey={process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY ?? ""}
          email={quote.email}
          amount={totalPremium * 100}
          text={"Make Payment"}
          className="PaystackButtonID hidden"
          reference={paystackReference}
          metadata={{
            custom_fields: [
              {
                display_name: "Mobile Number",
                variable_name: "mobile_number",
                value: quote.phoneNumber,
              },
            ],
          }}
          firstname={firstName}
          lastname={lastName}
          onSuccess={onPaymentSuccess}
          onClose={onPaymentSuccess}
          data-custom-button="ps-btn"
        />
      </div>
    </PageFrameComponent>
  );
};
